import { cva, VariantProps } from 'class-variance-authority';
import { Component, JSX } from 'solid-js';
import { cn } from '~/lib/utils';

const buttonVariants = cva(
  'group inline-flex items-center justify-center gap-1.5 text-sm font-normal rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*=size-])]:size-4 [&_svg:not([class*=text-])]:text-current transition-colors',
  {
    variants: {
      variant: {
        primary:
          'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg focus-visible:ring-ring',
        secondary: 'bg-secondary hover:bg-secondary/80 text-secondary-foreground shadow-sm',
        tertiary: 'bg-tertiary hover:bg-tertiary/80 text-tertiary-foreground shadow-sm',
        destructive:
          'bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-sm focus-visible:ring-destructive',
        warning: 'bg-warning hover:bg-warning/90 text-warning-foreground shadow-sm',
        info: 'bg-info hover:bg-info/90 text-info-foreground shadow-sm',
        success: 'bg-success hover:bg-success/90 text-success-foreground shadow-sm',

        contrast:
          'bg-contrast/40 hover:bg-contrast/80 text-accent hover:text-contrast-foreground shadow-sm',
        accent: 'bg-accent hover:bg-accent/90 text-accent-foreground shadow-sm',

        outlinePrimary:
          'border border-primary text-primary bg-primary/0 hover:bg-primary/10 focus-visible:ring-ring hover:text-accent-foreground group-[.toast]:text-primary-foreground group-[.toast]:border-primary-foreground group-[.toast]:bg-transparent group-[.toast]:hover:bg-primary-foreground/10',
        outlineTertiary:
          'border border-tertiary text-tertiary bg-tertiary/0 hover:bg-tertiary/10 focus-visible:ring-ring hover:text-accent-foreground group-[.toast]:text-tertiary-foreground group-[.toast]:border-tertiary-foreground group-[.toast]:bg-transparent group-[.toast]:hover:bg-tertiary-foreground/10',
        outlineWarning:
          'border border-warning text-warning bg-warning/0 hover:bg-warning/10 focus-visible:ring-ring hover:text-accent-foreground group-[.toast]:text-warning-foreground group-[.toast]:border-warning-foreground group-[.toast]:bg-transparent group-[.toast]:hover:bg-warning-foreground/10',

        darkPrimary:
          'bg-surface hover:bg-surface-light/20 text-primary hover:text-primary-light focus-visible:ring-ring',
        darkTertiary:
          'bg-surface hover:bg-surface-light/20 text-tertiary hover:text-tertiary-light focus-visible:ring-ring',
        darkDestructive:
          'bg-surface hover:bg-surface-light/20 text-destructive hover:text-destructive-light focus-visible:ring-ring',
        darkInfo:
          'bg-surface hover:bg-surface-light/20 text-info hover:text-info-light focus-visible:ring-ring',
        darkWarning:
          'bg-surface hover:bg-surface-light/20 text-warning hover:text-warning-light focus-visible:ring-ring',
        darkSuccess:
          'bg-surface hover:bg-surface-light/20 text-success hover:text-success-light focus-visible:ring-ring',

        mutedPrimary: 'bg-primary/30 hover:bg-primary/30 text-primary focus-visible:ring-ring',
        mutedTertiary: 'bg-tertiary/30 hover:bg-tertiary/30 text-tertiary focus-visible:ring-ring',
        mutedDestructive:
          'bg-destructive/30 hover:bg-destructive/30 text-destructive focus-visible:ring-ring',
        mutedInfo: 'bg-info/30 hover:bg-info/30 text-info focus-visible:ring-ring',
        mutedWarning: 'bg-warning/30 hover:bg-warning/30 text-warning focus-visible:ring-ring',
        mutedSuccess: 'bg-success/30 hover:bg-success/30 text-success focus-visible:ring-ring',
        muted: 'bg-muted/60 hover:bg-muted/90 text-muted-foreground shadow-sm',

        outline:
          'text-accent border border-input bg-transparent hover:bg-muted/40 hover:text-accent-foreground',
        ghost: 'bg-muted/0 hover:bg-contrast text-muted-foreground hover:text-contrast-foreground',
        ghostContrast: 'bg-muted/0 hover:bg-muted/40 text-contrast hover:text-muted-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        neutral: 'p-0 m-0 bg-transparent text-foreground',
      },
      size: {
        xs: 'h-4 px-2 text-[9px] [&_svg:not([class*=size-])]:size-2',
        sm: 'h-6 px-2.5 text-xs [&_svg:not([class*=size-])]:size-3',
        default: 'h-8 px-3',
        lg: 'h-10 px-5 text-normal gap-2 [&_svg:not([class*=size-])]:size-5',
        xl: 'h-16 px-8 text-xl rounded-full gap-3 [&_svg:not([class*=size-])]:size-5',
        'icon-xs': 'size-5 rounded-md [&_svg:not([class*=size-])]:size-2.5',
        'icon-sm': 'size-7 rounded-lg [&_svg:not([class*=size-])]:size-3',
        icon: 'size-8',
        'icon-lg': 'size-11 rounded-xl [&_svg:not([class*=size-])]:size-5',
      },
      effect: {
        none: '',
        hoverScale: 'hover:scale-105 transition-transform duration-200',
        elevated: 'border-t border-t-white/20 border-b border-b-black active:border-t-transparent',
        magic:
          'bg-gradient-to-br from-primary to-primary-dark hover:from-primary-light hover:to-primary text-primary-foreground transition-all duration-200 transform hover:scale-105 hover:shadow-xl shadow-primary/25 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      effect: 'none',
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export type ButtonProps = ButtonVariants & {
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  class?: string;
  children: JSX.Element;
  title?: string;
  style?: JSX.CSSProperties | string | undefined;
};

export const Button: Component<ButtonProps> = (props) => {
  return (
    <button
      disabled={props.disabled || props.loading}
      class={cn(
        buttonVariants({ variant: props.variant, size: props.size, effect: props.effect }),
        props.class
      )}
      style={props.style}
      onClick={props.onClick}
      title={props.title}
    >
      {props.loading ? (
        <div class="flex items-center">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          {props.loadingText || 'Chargement...'}
        </div>
      ) : (
        props.children
      )}
    </button>
  );
};
